import { useMemo } from 'react';
import { formatDate } from 'utils/helpers';
import get from 'lodash/get';
import { CBadge } from '@coreui/react';
import DataTable from 'components/common/DataTable';
import CIcon from '@coreui/icons-react';
import { FormattedMessage } from 'react-intl';
import { cibAdobeAcrobatReader } from '@coreui/icons';
import Pagination from 'components/common/Pagination';
import type { IntlShape } from 'react-intl';
import type { TProject, TProjects } from 'hooks/projects/useGetProjects';
import requireValue from 'utils/requireValue';
import s from './ProjectsTable.module.scss';

/** A row of the projects table. */
interface IProjectRow extends Omit<
  TProject,
  '_id' | 'user' | 'created_at' | 'last_updated_at' | 'type'
> {
  id: string;
  type: string;
  creator: string;
  email: string | null | undefined;
  registrationDate: string;
  created: string;
  lastUpdated: string;
}

interface IProjectsTableProps {
  projects: TProjects;
  changePage: (page: number) => void;
  currentPage: number;
  total: number | null;
  t: IntlShape['formatMessage'];
}

const creatorOf = (user: TProject['user'], t: IntlShape['formatMessage']): string => {
  if (!user) return t({ id: 'projects.not_assigned' });
  const { firstName, lastName } = requireValue(user.profile, 'firstName');
  return `${firstName} ${lastName}`;
};

const ProjectsTable = ({ projects, changePage, currentPage, total, t }: IProjectsTableProps) => {
  const tableHead = useMemo(
    () => [
      { key: 'name', label: t({ id: 'projects.name' }) },
      { key: 'type', label: t({ id: 'projects.type' }) },
      { key: 'price', label: t({ id: 'projects.price' }) },
      { key: 'creator', label: t({ id: 'projects.creator' }) },
      { key: 'registrationDate', label: t({ id: 'projects.registration_date' }) },
      { key: 'created', label: t({ id: 'projects.created' }) },
      { key: 'lastUpdated', label: t({ id: 'projects.last_updated' }) },
      { key: 'clientIP', label: t({ id: 'projects.clientIP' }) },
      { key: 'pdf', label: t({ id: 'projects.pdf' }) },
    ],
    [t],
  );

  const items = useMemo(
    () =>
      // A missing list, item or creator profile throws, as it did.
      requireValue(projects, 'map').map((project): IProjectRow => {
        const { _id, user, created_at, last_updated_at, type, ...rest } = requireValue(
          project,
          '_id',
        );
        return {
          id: _id,
          type: t({ id: `projects.types.${type}` }),
          creator: creatorOf(user, t),
          email: get(user, 'emails[0].address'),
          registrationDate: user?.createdAt ? formatDate(user.createdAt) : '–',
          created: formatDate(created_at),
          lastUpdated: formatDate(last_updated_at),
          ...rest,
        };
      }),
    [projects, t],
  );

  return (
    <>
      <DataTable
        addTableClasses={s.projectsTable}
        hover
        items={items}
        fields={tableHead}
        size="sm"
        scopedSlots={{
          name: ({ name, archived }: IProjectRow) => (
            <td>
              <span>
                {name}{' '}
                {archived && (
                  <CBadge color="warning">
                    <FormattedMessage id="projects.archived" />
                  </CBadge>
                )}
              </span>
            </td>
          ),
          creator: ({ creator, email }: IProjectRow) => (
            <td>
              <span>{creator}</span> {email ? <a href={`mailto:${email}`}>{email}</a> : ''}
            </td>
          ),
          pdf: ({ pdf }: IProjectRow) => (
            <td>
              {/* React leaves out an href of null or undefined alike. */}
              <a href={pdf ?? undefined} target="_blank" rel="noopener noreferrer">
                <div className={s.pdfButton}>
                  <CIcon icon={cibAdobeAcrobatReader} />
                </div>
              </a>
            </td>
          ),
        }}
      />
      <Pagination {...{ changePage, currentPage, total }} />
    </>
  );
};

export default ProjectsTable;
